import { useState } from 'react';
import { Text, Flex, Avatar, ButtonIcon, Link } from '@backstage/ui';
import { useEntity, getEntityRelations } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { RELATION_MEMBER_OF } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import {
  RiRefreshLine,
  RiMailLine,
  RiTeamLine,
} from '@remixicon/react';
import styles from './profiles.module.css';

export const UserProfileCard = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const { t } = useTranslationRef(appTranslationRef);
  const [refreshing, setRefreshing] = useState(false);

  const spec = entity.spec as Record<string, unknown> | undefined;
  const profile = spec?.profile as Record<string, string> | undefined;

  const displayName = profile?.displayName || entity.metadata.title || entity.metadata.name;
  const email = profile?.email || undefined;
  const picture = profile?.picture || undefined;

  const memberOfGroups = getEntityRelations(entity, RELATION_MEMBER_OF, { kind: 'group' });

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
      await catalogApi.refreshEntity(entityRef);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to refresh entity:', error);
    } finally {
      setTimeout(() => setRefreshing(false), 1000);
    }
  };

  return (
    <div className={styles.userHero}>
      <div className={styles.userHeroContent}>
        <div className={styles.userHeroTop}>
          <div className={styles.userAvatarWrap}>
            <Avatar
              src={picture || ''}
              name={displayName}
              size="large"
            />
          </div>

          <div className={styles.userInfo}>
            <Text variant="title-small" weight="bold">
              {displayName}
            </Text>
            {email && (
              <Flex gap="2" align="center">
                <RiMailLine size={13} className={styles.userEmailIcon} />
                <Text variant="body-small" className={styles.userEmail}>
                  {email}
                </Text>
              </Flex>
            )}
          </div>

          <ButtonIcon
            icon={<RiRefreshLine size={16} />}
            onPress={handleRefresh}
            isDisabled={refreshing}
            variant="tertiary"
            size="small"
            aria-label={t('userProfile.refresh')}
          />
        </div>

        {memberOfGroups.length > 0 && (
          <div className={styles.userGroups}>
            <Flex gap="2" className={styles.flexWrap}>
              {memberOfGroups.map(group => (
                <Link
                  key={group.name}
                  href={`/catalog/${group.namespace || 'default'}/group/${group.name}`}
                  className={styles.groupChip}
                >
                  <RiTeamLine size={14} />
                  <Text variant="body-small">
                    {group.name}
                  </Text>
                </Link>
              ))}
            </Flex>
          </div>
        )}
      </div>
    </div>
  );
};
